import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Card, CardBody, Col, Container, Row,
  Form, FormGroup, Label, Input, Button, CardHeader, Spinner
} from 'reactstrap';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useHistory, useParams } from "react-router-dom";
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../BaseUrl/cropUtils';
import ApiClient from '../BaseUrl/ApiClient';

function UpdateCategories() {
  const history = useHistory();
  const { id } = useParams();

  const [initialData, setInitialData] = useState({
    name: '',
    categoryPhotoUrl: '',
    categoryPhotoFile: null
  });
  const [formData, setFormData] = useState({
    name: '',
    categoryPhoto: null,
    removeLogo: false,
    currentPhotoUrl: '',
    currentPhotoFile: null // Store the existing photo file
  });
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [selectedFileName, setSelectedFileName] = useState('No file chosen');
  const fileInputRef = useRef(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  const hasChanges = useCallback(() => {
    const nameChanged = formData.name !== initialData.name;
    const photoChanged = formData.categoryPhoto !== null;
    const photoRemoved = formData.removeLogo !== false;
    
    return nameChanged || photoChanged || photoRemoved;
  }, [formData, initialData]);

  useEffect(() => {
    const fetchCategory = async () => {
      if (!id) {
        toast.error('Category ID is missing.');
        return;
      }

      try {
        setCategoryLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}admin/categories/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const category = data.responseData?.category;

        if (category) {
          const photoUrl = category.categoryLogo?.url || '';
          
          // Convert existing photo URL to a File object
          let photoFile = null;
          if (photoUrl) {
            try {
              const photoBlob = await fetch(photoUrl).then(r => r.blob());
              photoFile = new File([photoBlob], 'existing-photo.jpg', { type: 'image/jpeg' });
            } catch (error) {
              console.error('Error converting photo URL to File:', error);
            }
          }

          setInitialData({
            name: category.name || '',
            categoryPhotoUrl: photoUrl,
            categoryPhotoFile: photoFile
          });
          setFormData({
            name: category.name || '',
            categoryPhoto: null,
            removeLogo: false,
            currentPhotoUrl: photoUrl,
            currentPhotoFile: photoFile
          });
        } else {
          throw new Error('Category data not found');
        }
      } catch (error) {
        console.error('Error fetching category:', error);
        toast.error(`Failed to load category: ${error.message}`);
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategory();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFileName(file.name); 
      const reader = new FileReader();

      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropper(true);
        e.target.value = '';
      };

      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      categoryPhoto: null,
      removeLogo: true,
      currentPhotoUrl: '',
      currentPhotoFile: null
    }));
    setSelectedFileName('No file chosen');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveCrop = async () => {
    try {
      const croppedImage = await getCroppedImg(
        imageToCrop,
        croppedAreaPixels,
        'category-photo.jpeg'
      );
      const file = new File([croppedImage], 'category-photo.jpeg', {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      setFormData(prev => ({
        ...prev,
        categoryPhoto: file,
        removeLogo: false,
        currentPhotoUrl: URL.createObjectURL(croppedImage),
        currentPhotoFile: file
      }));

      setShowCropper(false);
    } catch (error) {
      console.error('Error cropping image:', error);
      toast.error('Failed to crop image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasChanges()) {
      toast.info('No changes detected');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Always include the current name
      formDataToSend.append('name', formData.name.trim());
      
      // Handle photo scenarios
      if (formData.removeLogo) {
        formDataToSend.append('removeLogo', 'true');
      } else if (formData.categoryPhoto) {
        // New photo uploaded
        formDataToSend.append('categoryPhoto', formData.categoryPhoto);
      } else {
        // Send existing photo file if available
        if (formData.currentPhotoFile) {
          formDataToSend.append('categoryPhoto', formData.currentPhotoFile);
        }
      }

      const response = await ApiClient().put(`admin/categories/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;

      if (data.responseCode === 200 || (data.responseMessage && data.responseMessage.some(msg => msg.toLowerCase().includes('success')))) {
        toast.success('Category updated successfully!');
        setTimeout(() => {
          history.push('/all-categories');
        }, 1500);
      } else {
        throw new Error(data.responseMessage?.join(', ') || 'Failed to update category');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage?.join(', ') || error.message;
      toast.error(`${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Update Category</title>
        </MetaTags>
        <ToastContainer />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader className='d-flex align-items-center'>
                <Link to="/all-categories">
                  <Button color="primary" className="btn">
                    <i className="mdi mdi-arrow-left"></i>
                  </Button>
                </Link>
                <h4 className="card-title mb-0 ms-2">Update Category</h4>
              </CardHeader>
              <CardBody>
                {categoryLoading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                    <span className="ms-2">Loading category data...</span>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Name *</Label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter category name"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Category Photo</Label>
                          <div className="d-flex">
                            <Button
                              type="button"
                              color="primary"
                              onClick={() => fileInputRef.current.click()}
                              style={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0
                              }}
                            >
                              Choose File
                            </Button>

                            <Input
                              type="text"
                              readOnly
                              value={selectedFileName}
                              style={{
                                flex: 1, 
                                cursor: "pointer", 
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0
                              }}
                              onClick={() => fileInputRef.current.click()}
                            />

                            <input
                              type="file"
                              name="file"
                              onChange={handleFileChange}
                              ref={fileInputRef}
                              accept="image/*"
                              style={{ display: "none" }}
                            />
                          </div>
                          {formData.currentPhotoUrl && !formData.removeLogo && (
                            <div className="mt-1">
                              <p>Preview Photo:</p>
                              <div className="d-flex align-items-center mt-1">
                                <img
                                  src={formData.currentPhotoUrl}
                                  alt="Category Preview"
                                  className="me-2"
                                  style={{
                                    maxWidth: '50%',
                                    maxHeight: '50px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                  }}
                                />
                                <Button
                                  color="danger"
                                  size="sm"
                                  className="btn"
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "50%",
                                    backgroundColor: "transparent",
                                    color: "rgb(189, 9, 9)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    marginBottom: "25px",
                                    boxShadow: "none"
                                  }}
                                  onClick={handleRemoveLogo}
                                >
                                  x
                                </Button>
                              </div>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading || categoryLoading || !hasChanges()}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" /> Updating...
                          </>
                        ) : 'Update Category'}
                      </Button>
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <h4>Crop your category photo</h4>

              <div className="crop-container">
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={3 / 2}
                  cropShape="rect"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="crop-controls" style={{ margin: '15px 0' }}>
                <label>
                  Zoom: {zoom.toFixed(1)}x
                </label>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="crop-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button
                  color="secondary"
                  onClick={() => setShowCropper(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleSaveCrop}
                >
                  Save Crop
                </Button>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default UpdateCategories;